import { useQuery } from '@tanstack/react-query';
import { API_CONFIG } from '@/lib/api/config';
import { useAuth } from '@/components/AuthProvider';
import { useContext, useMemo } from 'react';
import { AuthContext } from '@/components/AuthProvider';
import { useCurrentSubscription } from '@/hooks/useCurrentSubscription';

interface FeatureLimitCheck {
  allowed: boolean;
  limit: number | null;
  usage: number;
  remaining: number | null;
  message?: string;
}

interface OrganizationEntitlements {
  organizationId: string;
  planId: string | null;
  planName: string;
  subscriptionId: string | null;
  subscriptionStatus: string;
  entitlements: {
    limits: Record<string, number>;
    features: Record<string, boolean>;
    sub_features?: Record<string, Record<string, number>>;
  };
}

export const ALWAYS_ENABLED_FEATURES = {
  inbox: true,
  scheduling_posts: true,
  lead_ads_sync: true,
  whatsapp_ads: true,
  calendly: true,
  segments: true,
  chat_history: true,
  message_reports: true,
  campaign_performance: true,
  ads_results: true,
  sales_and_conversions: true,
  billing: true,
  how_to_use: true,
  help_articles: true,
  raise_ticket: true,
  contact_support: true,
};

export function useOrganizationEntitlements() {
  const authContext = useContext(AuthContext);
  const user = authContext?.user || null;
  const organizationId = user?.organization_id;

  const query = useCurrentSubscription({ enabled: !!organizationId });

  const entitlements = useMemo<OrganizationEntitlements | undefined>(() => {
    if (!organizationId) return undefined;
    const sub: any = query.data;

    if (sub?.entitlements?.entitlements || sub?.entitlements?.limits) {
      const ent = sub.entitlements.entitlements || sub.entitlements;
      return {
        organizationId,
        planId: sub.plan?.id || sub.plan_id || null,
        planName: sub.plan?.name || 'Subscription',
        subscriptionId: sub.id || null,
        subscriptionStatus: sub.status || 'active',
        entitlements: {
          limits: ent.limits || {},
          features: { ...ent.features, ...ALWAYS_ENABLED_FEATURES },
          sub_features: ent.sub_features || {},
        },
      };
    }

    return {
      organizationId,
      planId: sub?.plan?.id || sub?.plan_id || null,
      planName: sub?.plan?.name || 'No Subscription',
      subscriptionId: sub?.id || null,
      subscriptionStatus: sub?.status || 'none',
      entitlements: {
        limits: {},
        features: { ...ALWAYS_ENABLED_FEATURES },
      },
    };
  }, [query.data, organizationId]);

  return {
    ...query,
    data: entitlements,
  } as ReturnType<typeof useQuery<OrganizationEntitlements>> & { data: OrganizationEntitlements | undefined };
}

export function useCanUseFeature(featureName: string) {
  const { data: entitlements } = useOrganizationEntitlements();

  const isEnabled = entitlements?.entitlements.features[featureName] === true;
  const limit = entitlements?.entitlements.limits[featureName] ??
    entitlements?.entitlements.limits[mapFeatureToLimitKey(featureName)];

  return {
    canUse: isEnabled && (limit === -1 || (limit !== null && limit !== undefined && limit > 0)),
    limit,
    isEnabled,
  };
}

function mapFeatureToLimitKey(featureName: string): string {
  const map: Record<string, string> = {
    contacts: 'contacts',
    templates: 'templates',
    campaigns: 'campaigns',
    whatsapp_flows: 'whatsapp_flows',
    automations: 'automation_flows',
    business_accounts: 'business_accounts',
    analytics: 'analytics_level',
    commerce: 'commerce',
    integrations: 'integrations',
    messages_per_month: 'messages_per_month',
    ai_generation: 'ai_generation',
    leads: 'leads',
    notes_and_tags: 'notes_and_tags',
    instagram_automation: 'instagram_automation',
    appointments: 'appointments',
    data_store: 'data_store',
  };
  return map[featureName] || featureName;
}

export function useFeatureUsage(featureName: string, businessAccountId?: string) {
  const authContext = useContext(AuthContext);
  const organizationId = authContext?.user?.organization_id;

  return useQuery<{ count: number; limit: number | null }>({
    queryKey: ['feature-usage', featureName, organizationId, businessAccountId],
    queryFn: async () => {
      if (!organizationId) {
        throw new Error('Organization ID not found');
      }

      const params = new URLSearchParams({
        feature: featureName,
        organizationId,
      });

      if (businessAccountId) {
        params.append('businessAccountId', businessAccountId);
      }

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/admin/organizations/${organizationId}/usage?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch feature usage');
      }

      const result = await response.json();
      return result.data;
    },
    enabled: !!organizationId,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 60 * 1000, // 1 minute
  });
}

export function getLimitErrorMessage(error: any): string {
  if (error?.response?.data?.error) {
    return error.response.data.error;
  }
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.error && typeof error.error === 'string') {
    return error.error;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error?.message) {
    return error.message;
  }
  return 'An error occurred. Please try again.';
}

export function isLimitExceededError(error: any): boolean {
  const errorMessage = getLimitErrorMessage(error).toLowerCase();
  return (
    errorMessage.includes('limit reached') ||
    errorMessage.includes('limit exceeded') ||
    errorMessage.includes('not available on your current plan') ||
    errorMessage.includes('upgrade') ||
    errorMessage.includes('feature is not enabled')
  );
}

